import { useState, useEffect } from "react";
import api from "../lib/api";
import { X, Loader2, Trash2 } from "lucide-react";

/**
 * BookingModal — pharmacy creates or edits a booking for a candidate.
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - candidate: {id, name} (when proposing a new booking)
 *  - booking: existing booking object (when editing); if present, candidate is ignored
 *  - onSaved: (booking) => void
 */
export default function BookingModal({ open, onClose, candidate, booking, onSaved, initialNeedId = "" }) {
  const editing = !!booking;
  const [needs, setNeeds] = useState([]);
  const [needId, setNeedId] = useState(booking?.need_id || initialNeedId);
  const [startDate, setStartDate] = useState(booking?.need_snapshot?.start_date || booking?.start_date || "");
  const [endDate, setEndDate] = useState(booking?.need_snapshot?.end_date || booking?.end_date || "");
  const [startTime, setStartTime] = useState(booking?.need_snapshot?.start_time || booking?.start_time || "09:00");
  const [endTime, setEndTime] = useState(booking?.need_snapshot?.end_time || booking?.end_time || "17:00");
  const [status, setStatus] = useState(booking?.status || "pending");
  const [notes, setNotes] = useState(booking?.notes || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open || editing) return;
    (async () => {
      try {
        const { data } = await api.get("/needs");
        const rows = Array.isArray(data) ? data : data.items || [];
        const open_ = rows.filter((n) => (n.status || "open") !== "closed");
        setNeeds(open_);
        if (initialNeedId) {
          const n = open_.find((x) => x.id === initialNeedId);
          if (n) applyNeed(n);
        }
      } catch { /* ignore */ }
    })();
  }, [open, editing]); // eslint-disable-line react-hooks/exhaustive-deps

  const applyNeed = (n) => {
    const ds = (n.dates || []).slice().sort();
    setStartDate(n.start_date || ds[0] || "");
    setEndDate(n.end_date || ds[ds.length - 1] || n.start_date || ds[0] || "");
    setStartTime(n.start_time || n.global_slots?.[0]?.start || "09:00");
    setEndTime(n.end_time || (n.global_slots || []).slice(-1)[0]?.end || "17:00");
  };

  // Sync defaults when picking another need
  const pickNeed = (id) => {
    setNeedId(id);
    const n = needs.find((x) => x.id === id);
    if (n) applyNeed(n);
  };

  const submit = async () => {
    setErr(""); setBusy(true);
    try {
      if (editing) {
        const patch = {
          start_date: startDate, end_date: endDate,
          start_time: startTime, end_time: endTime,
          status, notes,
        };
        const { data } = await api.patch(`/bookings/${booking.id}`, patch);
        onSaved?.(data); onClose?.();
      } else {
        if (!needId || !candidate?.id) { setErr("Choisissez un besoin et un candidat"); setBusy(false); return; }
        const { data: created } = await api.post("/bookings", { need_id: needId, replacement_id: candidate.id });
        // Optionally patch dates/times if different from the need
        const needsPatch = (startDate && startDate !== created.need_snapshot?.start_date) ||
                           (endDate && endDate !== created.need_snapshot?.end_date) ||
                           (startTime && startTime !== created.need_snapshot?.start_time) ||
                           (endTime && endTime !== created.need_snapshot?.end_time);
        let final = created;
        if (needsPatch) {
          const { data: patched } = await api.patch(`/bookings/${created.id}`, {
            start_date: startDate, end_date: endDate, start_time: startTime, end_time: endTime,
          });
          final = patched;
        }
        onSaved?.(final); onClose?.();
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || "Échec de l'enregistrement");
    } finally { setBusy(false); }
  };

  const removeBooking = async () => {
    if (!editing) return;
    if (!window.confirm("Supprimer cette mission ? Le candidat sera notifié.")) return;
    setBusy(true);
    try {
      await api.delete(`/bookings/${booking.id}`);
      onSaved?.(null); onClose?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Échec de la suppression");
    } finally { setBusy(false); }
  };

  if (!open) return null;
  const candidateLabel = editing ? (booking.replacement_name || "Candidat") : (candidate?.name || "Candidat");

  return (
    <div className="fixed inset-0 bg-black/30 grid place-items-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()} data-testid="booking-modal">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-base font-medium text-[#0F2A1B]" style={{ fontFamily: "Outfit" }}>
              {editing ? "Modifier la mission" : "Proposer une mission"}
            </h3>
            <div className="text-xs text-[#6B726A] mt-0.5">Candidat : <span className="font-medium text-[#0F2A1B]">{candidateLabel}</span></div>
          </div>
          <button onClick={onClose} className="w-7 h-7 grid place-items-center text-[#6B726A]" data-testid="booking-close"><X className="w-4 h-4" /></button>
        </div>

        {!editing && (
          <div className="mb-3">
            <div className="text-xs uppercase tracking-wider font-medium text-[#6B726A] mb-1">Besoin associé</div>
            <select value={needId} onChange={(e) => pickNeed(e.target.value)} data-testid="booking-need-select"
              className="w-full text-sm px-3 py-2 rounded-xl border border-[#E2E5E0]">
              <option value="">— Choisir un besoin —</option>
              {needs.length === 0 && <option value="" disabled>Aucun besoin ouvert — créez-en un d&apos;abord</option>}
              {needs.map((n) => {
                const ds = (n.dates || []).slice().sort();
                const d1 = n.start_date || ds[0] || "";
                const d2 = n.end_date || ds[ds.length - 1] || "";
                return (
                  <option key={n.id} value={n.id}>
                    {n.role_type || n.title || "Mission"}{d1 ? ` — ${d1}` : ""}{d2 && d2 !== d1 ? ` → ${d2}` : ""}
                  </option>
                );
              })}
            </select>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <div className="text-xs uppercase tracking-wider font-medium text-[#6B726A] mb-1">Du</div>
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="w-full text-sm px-3 py-2 rounded-xl border border-[#E2E5E0]" data-testid="booking-start-date" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider font-medium text-[#6B726A] mb-1">Au</div>
            <input type="date" value={endDate} min={startDate} onChange={(e) => setEndDate(e.target.value)} className="w-full text-sm px-3 py-2 rounded-xl border border-[#E2E5E0]" data-testid="booking-end-date" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider font-medium text-[#6B726A] mb-1">Début</div>
            <input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} className="w-full text-sm px-3 py-2 rounded-xl border border-[#E2E5E0]" data-testid="booking-start-time" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider font-medium text-[#6B726A] mb-1">Fin</div>
            <input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} className="w-full text-sm px-3 py-2 rounded-xl border border-[#E2E5E0]" data-testid="booking-end-time" />
          </div>
        </div>

        {editing && (
          <div className="mb-3">
            <div className="text-xs uppercase tracking-wider font-medium text-[#6B726A] mb-1">Statut</div>
            <div className="flex flex-wrap gap-2">
              {[
                { k: "pending",   l: "En attente", c: "bg-[#FEF3C7] text-[#92400E] border-[#FCD34D]" },
                { k: "confirmed", l: "Confirmée",  c: "bg-[#DBEAFE] text-[#1E3A8A] border-[#93C5FD]" },
                { k: "completed", l: "Terminée",   c: "bg-[#E8E5FF] text-[#3F2EB2] border-[#D9CCFB]" },
                { k: "cancelled", l: "Annulée",    c: "bg-[#FDE4E2] text-[#A53A2F] border-[#F5BFBA]" },
              ].map((s) => (
                <button key={s.k} type="button" onClick={() => setStatus(s.k)} data-testid={`booking-status-${s.k}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border ${status === s.k ? s.c : "border-[#E2E5E0] text-[#6B726A] hover:bg-[#F7F8F6]"}`}>
                  {s.l}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="mb-4">
          <div className="text-xs uppercase tracking-wider font-medium text-[#6B726A] mb-1">Notes (optionnel)</div>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="Précisions pour le candidat…"
            className="w-full text-sm px-3 py-2 rounded-xl border border-[#E2E5E0] resize-none" data-testid="booking-notes" />
        </div>

        {err && <div className="text-xs text-[#E3655B] mb-3" data-testid="booking-error">{err}</div>}

        <div className="flex gap-2">
          {editing && (
            <button onClick={removeBooking} disabled={busy} data-testid="booking-delete"
              className="px-3 py-2.5 rounded-full border border-[#F5BFBA] text-[#E3655B] hover:bg-[#FFF4F3] text-sm inline-flex items-center gap-1">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
          <button onClick={onClose} className="flex-1 text-sm px-4 py-2.5 rounded-full border border-[#E2E5E0] text-[#6B726A] hover:bg-[#F7F8F6]" data-testid="booking-cancel">Annuler</button>
          <button onClick={submit} disabled={busy || (!editing && !needId)} data-testid="booking-confirm"
            className="flex-1 text-sm px-4 py-2.5 rounded-full bg-[#1A4D2E] text-white hover:bg-[#143C24] disabled:opacity-60">
            {busy ? <Loader2 className="w-4 h-4 animate-spin inline" /> : (editing ? "Enregistrer" : "Proposer")}
          </button>
        </div>
      </div>
    </div>
  );
}
